import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useRefetchOnFocus } from '@redpanda/utils';
import { getNotifications, markNotificationsRead } from '../services/api';
import { PMNotification } from '../types/pm';

interface NotificationState {
  items: PMNotification[];
  unread: number;
  /** 還有更舊的通知（「載入更多」） */
  hasMore: boolean;
  loadingMore: boolean;
  load: () => Promise<void>;
  loadMore: () => Promise<void>;
  /** 標為已讀（沒帶 ids＝全部）：畫面先改，失敗再重抓 */
  markRead: (ids?: string[]) => Promise<void>;
}

// persist：重新整理時先顯示上次的通知與未讀數（照 pmDashboardStore），再由輪詢更新
export const useNotificationStore = create<NotificationState>()(persist((set, get) => ({
  items: [],
  unread: 0,
  hasMore: false,
  loadingMore: false,
  // 重抓最新 30 則；已經「載入更多」的舊通知保留在後面（每分鐘輪詢不會把往下載入的內容洗掉）
  // 只有接得上時才保留：新的一頁最後一則也在目前列表裡；接不上（離開期間新通知超過 30 則）就從第一頁重來，避免中間漏掉一段
  load: async () => {
    try {
      const { data } = await getNotifications();
      const latest: PMNotification[] = data.items;
      const current = get().items;
      const joinAt = latest.length > 0 ? current.findIndex(n => n.id === latest[latest.length - 1].id) : -1;
      const loadedOlder = joinAt >= 0 ? current.slice(joinAt + 1) : [];
      set({ items: [...latest, ...loadedOlder], unread: data.unread, hasMore: loadedOlder.length > 0 ? get().hasMore : data.hasMore });
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  },
  loadMore: async () => {
    const { items, loadingMore } = get();
    if (loadingMore || items.length === 0) return;
    set({ loadingMore: true });
    try {
      const { data } = await getNotifications(items[items.length - 1].createdAt);
      set(state => ({ items: [...state.items, ...data.items], hasMore: data.hasMore }));
    } catch (error) {
      console.error('Failed to load more notifications:', error);
    } finally {
      set({ loadingMore: false });
    }
  },
  markRead: async (ids) => {
    const now = new Date().toISOString();
    const targets = new Set(ids ?? get().items.map(n => n.id));
    set(state => ({
      items: state.items.map(n => (targets.has(n.id) && !n.readAt ? { ...n, readAt: now } : n)),
      unread: ids ? Math.max(0, state.unread - state.items.filter(n => targets.has(n.id) && !n.readAt).length) : 0,
    }));
    try {
      await markNotificationsRead(ids);
    } catch (error) {
      console.error('Failed to mark notifications read:', error);
      get().load();
    }
  },
}), {
  name: 'pm-notification-storage',
  // 只保留最新 30 則（重新整理後從第一頁開始，舊的再按「載入更多」）
  partialize: (state) => ({ items: state.items.slice(0, 30), unread: state.unread, hasMore: state.items.length > 30 || state.hasMore }),
}));

const POLL_MS = 60_000;

/** 側欄未讀數：進來抓一次，之後每分鐘（分頁在背景時暫停）與切回分頁時更新 */
export const useNotificationPolling = () => {
  const load = useNotificationStore(state => state.load);
  useEffect(() => {
    load();
    const timer = setInterval(() => {
      if (!document.hidden) load();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);
  useRefetchOnFocus(load, 0);
};
